import { ApolloClient, createHttpLink, InMemoryCache, NormalizedCacheObject, gql, ApolloCache } from "@apollo/client";
import config from "../../../config";

export type AssetMap = { [key: string]: any };

export type MutationBlob = {
	blob: Blob;
	fileName: string;
}

type ExtractedMutationBlob = {
	path: string;
	value: MutationBlob;
}

const emptyCacheObj = {};
export class VoidCache extends ApolloCache<NormalizedCacheObject> 
{
	read(options) { return null; }
	write(options) { return undefined; }
	diff(options) { return {}; }
	watch(watch) { return ()=>{}; }
    async reset() {} // eslint-disable-line
	evict(options) { return false; }
	restore(data) { return this; }
	extract(optimistic) { return emptyCacheObj; }
	removeOptimistic(id) {}
	batch(options) { return undefined as any; }
	performTransaction(update, optimisticId) {}
	recordOptimisticTransaction(transaction, optimisticId) {}
	transformDocument(document) { return document; }
	transformForLink(document) { return document; }
	identify(object) { return undefined; }
	gc() { return [] as string[]; }
	modify(options) { return false; }
	readQuery(options, optimistic?) { return null; }
	readFragment(options, optimistic?) { return null; }
	writeQuery(opts) { return undefined; }
	writeFragment(opts) { return undefined; }
	updateQuery(options, update) { return null; }
	updateFragment(options, update) { return null; }
}

export class ApolloGQLClient 
{
	private static log = window._env_.ENVIRONMENT === "development" ? console : { info: () => {} };
	private static _jwt: string;
	private static _permalink: string = "";
	private static _client: ApolloClient<NormalizedCacheObject>;
	private static gqlApiUri = `${config.CMS_PROTOCOL}://${config.CMS_HOST}${config.CMS_GQL_ROUTE}?permalink=`;

	static set jwt(token: string) 
	{
		this._jwt = token;
	}

	static set permalink(permalink: string)
	{
		this._permalink = permalink;
	}

	static set client(client: ApolloClient<NormalizedCacheObject>)
	{
		this._client = client;
	}

	static get client(): ApolloClient<NormalizedCacheObject>
	{
		return this._client;
	}

	static get permalink(): string
	{
		return this._permalink;
	}

	static get jwt(): string
	{
		if (!this._jwt) 
		{
			throw new Error('JWT token not set. Please set the token before making requests.');
		}
		return this._jwt;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private static async apolloClient(customFetchOptions: any = {}): Promise<ApolloClient<NormalizedCacheObject>>
	{
		const uri = await this.getUrl();
		const headers = {
			Authorization: `Bearer ${this.jwt}`,
			'User-Agent': 'Create2 API'
		};

		// Make sure the authorization header is included when
		// custom fetch options are provided
		if (customFetchOptions?.headers) 
		{
			customFetchOptions.headers = {
				...customFetchOptions.headers,
				...headers
			}
		}

		const link = createHttpLink({
			uri,
			headers,
			fetch: (uri, options) => 
			{
				// Merge the custom fetch options with Apollo's options
				// Here, this is used to create multipart requests in order to
				// upload files
				const fetchOptions = { ...options, ...customFetchOptions };
				return fetch(uri as RequestInfo, fetchOptions as RequestInit);
			}
		});

		if(!this._client)
		{
			this._client = new ApolloClient({
				uri,
				cache: new VoidCache(),
				link,
				defaultOptions: {
					watchQuery: {
						fetchPolicy: 'no-cache',
						errorPolicy: 'ignore',
					},
					query: {
						fetchPolicy: 'no-cache',
						errorPolicy: 'all',
					},
					mutate: {
						fetchPolicy: 'no-cache',
						errorPolicy: 'all',
					}
				}
			});
		}
		else 
		{
			this._client.setLink(link);
		}
		return this._client;
	}

	/**
 * Helpers
 * */
	private static async getUrl()
	{
		return `${ApolloGQLClient.gqlApiUri}${ApolloGQLClient.permalink}`;
	}

	static async mutate<T>(mutation: string, variables: any = {}, description: string)
	{
		this.log.info({
			mutation,
			variables
		}, description);

		const extractedMutationBlobs: ExtractedMutationBlob[] = [];
		this.extractAndNullifyMutationBlobsWithPaths(variables, extractedMutationBlobs);

		let apolloClientOptions = {}
		if (extractedMutationBlobs.length > 0)
		{
			const form = new FormData();
			form.append('operations', JSON.stringify({
				query: mutation,
				variables
			}));
	
			extractedMutationBlobs.forEach((extractedMutationBlob, index) => 
			{
				form.append('map', JSON.stringify({ [index]: [`variables.${extractedMutationBlob.path}`] }));
				form.append(`${index}`, extractedMutationBlob.value.blob, extractedMutationBlob.value.fileName);
			});

			apolloClientOptions = {
				method: 'POST',
				body: form,
				headers: form
			}
		}

		const client = await this.apolloClient(apolloClientOptions);
		const responseGql = await client
			.mutate({
				mutation: gql`${mutation}`,
				variables
			});

		return responseGql.data;
	}

	static async query(query: string, variables: any = {}, description: string, signal?: AbortSignal)
	{
		this.log.info({
			query,
			variables
		}, description);

		const client = await this.apolloClient();
		const responseGql = await client
			.query({
				query: gql`${query}`,
				variables,
				context: {
					fetchOptions: {
						signal
					}
				}
			});

		return responseGql.data;
	}

	/**
	 * Extracts mutation blobs from the object and nullifies them.
	 * This is used to create a form data object for the multipart request.
	 * @param obj - The object to extract mutation blobs from.
	 * @param extracted - The array to store the extracted mutation blobs.
	 * @param currentPath - The current path in the object.
	 * @returns The object with the mutation blobs nullified.
	 */
	static extractAndNullifyMutationBlobsWithPaths(obj: any, extracted: ExtractedMutationBlob[] = [], currentPath: string = '')
	{
		if (Array.isArray(obj))
		{
		  return obj.map((item, index) =>
		  {
				const path = `${currentPath}[${index}]`;
				if (this.isMutationBlob(item))
				{
				  extracted.push({ path, value: item });
				  return null;
				}
				return this.extractAndNullifyMutationBlobsWithPaths(item, extracted, path);
		  });
		}
	  
		if (obj && typeof obj === 'object' && !(obj instanceof Blob))
		{
		  for (const key in obj)
		  {
				const val = obj[key];
				const path = currentPath ? `${currentPath}.${key}` : key;
	  
				if (this.isMutationBlob(val))
				{
				  extracted.push({ path, value: val });
				  obj[key] = null;
				}
				else
				{
					obj[key] = this.extractAndNullifyMutationBlobsWithPaths(val, extracted, path);
				}
		  }
		}
	  
		return obj;
	}
	  

	static isMutationBlob(value: any)
	{
		return (
		  value &&
		  typeof value === 'object' &&
		  value.blob instanceof Blob &&
		  typeof value.fileName === 'string'
		);
	}
}